import { useConceptHistory, useUpdateConcept, useDeleteConceptVersion, useClearConceptHistory } from "@/hooks/useConcepts";
import { History, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { useRestoreVersion } from "@/hooks/useFacets";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { toastFacetError } from "@/components/angles/toastFacetError";
import { toast } from "@/lib/toast";

interface Props {
  conceptId: string;
  currentName?: string;
}

export function ConceptHistory({ conceptId, currentName }: Props) {
  const { data: versions } = useConceptHistory(conceptId);
  const updateConcept = useUpdateConcept(conceptId);
  const deleteVersion = useDeleteConceptVersion(conceptId);
  const clearHistory = useClearConceptHistory(conceptId);
  const [expanded, setExpanded] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const { data: me } = useCurrentUser();
  const facetsOn = !!me?.features?.facets;
  const restoreVersion = useRestoreVersion(conceptId);
  const navigate = useNavigate();
  const restoring = facetsOn ? restoreVersion.isPending : updateConcept.isPending;

  if (!versions || versions.length === 0) return null;

  const displayed = expanded ? versions : versions.slice(0, 3);

  const handleRestore = (version: typeof versions[0]) => {
    if (facetsOn) {
      // Restores the project question AND the version's angles (1c-1 endpoint).
      restoreVersion.mutate(version, {
        onSuccess: () => toast.success("Version restored. Scores will refresh in the background."),
        onError: (e) => void toastFacetError(e, "Couldn't restore that version.", () => navigate({ to: "/settings/billing" })),
        onSettled: () => setConfirmId(null),
      });
      return;
    }
    updateConcept.mutate(
      { name: version.name, abstract: version.abstract, keywords: version.keywords, skip_version: true },
      { onSettled: () => setConfirmId(null) },
    );
  };

  const handleDelete = (versionId: string) => {
    deleteVersion.mutate(versionId, {
      onSettled: () => setConfirmDeleteId(null),
    });
  };

  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <History className="size-3" />
        Version History ({versions.length})
      </p>
      <div className="space-y-2">
        {displayed.map((v, idx) => (
          <div
            key={v.id}
            className={`rounded-md border px-3 py-2 text-xs ${
              idx === 0
                ? "border-primary/40 bg-primary/5"
                : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-medium">
                {idx === 0 && currentName ? currentName : v.name}
                {v.change_source === "ai_refine" && (
                  <span className="ml-1 text-xs text-blue-500" title="AI refinement">✨</span>
                )}
                {idx === 0 && (
                  <span className="rounded bg-primary/10 px-1 py-0.5 text-[10px] font-normal text-primary">
                    Latest
                  </span>
                )}
              </span>
            </div>
            {v.abstract && (
              <p className="mt-1 text-muted-foreground line-clamp-2">
                {v.abstract.replace(/<[^>]+>/g, "").slice(0, 200)}
              </p>
            )}
            {v.keywords && (
              <p className="mt-1 text-muted-foreground">
                Keywords: {v.keywords}
              </p>
            )}
            {facetsOn && (() => {
              const active = (v.facets ?? []).filter((f) => !f.retired_at);
              return active.length >= 2 ? (
                <p className="mt-1 text-muted-foreground">Angles: {active.map((f) => f.label).join(" · ")}</p>
              ) : null;
            })()}
            <div className="mt-1.5 flex items-center gap-1">
              {confirmId === v.id ? (
                <span className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Restore this version?</span>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => handleRestore(v)}
                    disabled={restoring}
                    className="text-xs"
                  >
                    Yes
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setConfirmId(null)}
                    className="text-xs"
                  >
                    No
                  </Button>
                </span>
              ) : confirmDeleteId === v.id ? (
                <span className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Delete this version?</span>
                  <Button
                    variant="destructive"
                    size="xs"
                    onClick={() => handleDelete(v.id)}
                    disabled={deleteVersion.isPending}
                    className="text-xs"
                  >
                    Yes
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setConfirmDeleteId(null)}
                    className="text-xs"
                  >
                    No
                  </Button>
                </span>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setConfirmId(v.id)}
                    className="text-xs text-muted-foreground"
                  >
                    <RotateCcw className="size-3" />
                    Restore
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setConfirmDeleteId(v.id)}
                    className="text-xs text-muted-foreground"
                  >
                    <Trash2 className="size-3" />
                    Delete
                  </Button>
                  <span className="ml-auto text-muted-foreground">
                    {(() => {
                      const d = new Date(v.changed_at);
                      const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                      const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                      return `${date} @ ${time}`;
                    })()}
                  </span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        {versions.length > 3 && (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setExpanded(!expanded)}
            className="text-xs"
          >
            {expanded ? "Show less" : `Show all ${versions.length} versions`}
          </Button>
        )}
        {versions.length > 1 && (
          confirmClear ? (
            <span className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">Clear history?</span>
              <Button
                variant="destructive"
                size="xs"
                onClick={() => clearHistory.mutate(undefined, { onSettled: () => setConfirmClear(false) })}
                disabled={clearHistory.isPending}
                className="text-xs"
              >
                Yes
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setConfirmClear(false)}
                className="text-xs"
              >
                No
              </Button>
            </span>
          ) : (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setConfirmClear(true)}
              className="ml-auto text-xs text-muted-foreground"
            >
              <Trash2 className="size-3" />
              Clear
            </Button>
          )
        )}
      </div>
    </div>
  );
}
